// defaultToolImpls — Referens-implementationer av diagram-authoring-tools
// som dispatcher:n kan köra utan LLM-integration. Används primärt för
// tester + deterministiska skill-dry-runs. Produktion kan svepa in annan
// impl (t.ex. som skriver direkt till aiStagingSession).

import type { ToolImpl } from '@/lib/aiSkills/dispatcher';
import {
  planDiagram,
  createSheetDraft,
  batchCreateBlocks,
  batchCreateConnectors,
  createCrossSheetLink,
  createGroupDraft,
  validateDiagram,
  type BlockDraftInput,
  type ConnectorDraftInput,
} from '@/lib/aiDiagramTools';
import type {
  BlockType,
  CrossSheetConnector,
  MultiSheetWorkflowState,
  PortSide,
} from '@/types/workflow';

/** Interna multi-sheet-state som default-impls bygger upp. */
interface InMemoryState extends MultiSheetWorkflowState {
  sheets: MultiSheetWorkflowState['sheets'];
}

/** Narrow:a ur unknown-args med defaults. */
function s(v: unknown): string { return typeof v === 'string' ? v : ''; }
function n(v: unknown): number { return typeof v === 'number' ? v : 0; }
function a<T>(v: unknown): T[] { return Array.isArray(v) ? v as T[] : []; }

export function buildDefaultToolImpls(
  workspace: { current: InMemoryState } = { current: {
    name: 'ai-diagram',
    sheets: [],
    crossSheetConnectors: [],
    activeSheetIndex: 0,
  } },
): Record<string, ToolImpl> {
  return {
    plan_diagram: (args) => {
      const plan = planDiagram(s(args.goal), {
        sheetCount: args.sheet_count as number | undefined,
        style: args.style as 'architecture' | undefined,
      });
      return { ok: true, result: plan };
    },
    create_sheet: (args, state) => {
      const sheet = createSheetDraft({
        name: s(args.name),
        meta: {
          description: s(args.description) || undefined,
          color: s(args.color) || undefined,
          icon: s(args.icon) || undefined,
        },
      });
      workspace.current.sheets.push(sheet);
      // sheet_ref (client-provided) → sheet.id så senare batch-calls kan referera.
      const ref = s(args.sheet_ref) || sheet.id;
      state.sheetsByRef.set(ref, sheet.id);
      state.counts.sheets++;
      return { ok: true, result: { sheetId: sheet.id, ref } };
    },
    batch_create_blocks: (args, state) => {
      const sheetRef = s(args.sheet_ref) || s(args.sheet_id);
      const sheetId = state.sheetsByRef.get(sheetRef) ?? sheetRef;
      const sheet = workspace.current.sheets.find(x => x.id === sheetId);
      if (!sheet) return { ok: false, error: `unknown-sheet: ${sheetRef}` };
      const inputs = a<Partial<BlockDraftInput> & { type: BlockType }>(args.blocks).map(b => ({
        ref: s(b.ref),
        type: (b.type ?? 'general') as BlockType,
        x: n(b.x),
        y: n(b.y),
        config: b.config ?? { name: 'Node' },
        color: b.color,
      } as BlockDraftInput));
      const { blocks, idMap } = batchCreateBlocks(inputs);
      sheet.blocks.push(...blocks);
      for (const [ref, id] of Object.entries(idMap)) {
        state.blockIdMap.set(ref, id);
      }
      state.counts.blocks += blocks.length;
      return { ok: true, result: { count: blocks.length, idMap } };
    },
    batch_create_connectors: (args, state) => {
      const sheetRef = s(args.sheet_ref) || s(args.sheet_id);
      const sheetId = state.sheetsByRef.get(sheetRef) ?? sheetRef;
      const sheet = workspace.current.sheets.find(x => x.id === sheetId);
      if (!sheet) return { ok: false, error: `unknown-sheet: ${sheetRef}` };
      const idMap = Object.fromEntries(state.blockIdMap.entries());
      const existingIds = sheet.blocks.map(b => b.id);
      const inputs = a<ConnectorDraftInput>(args.edges);
      const { connectors, rejected } = batchCreateConnectors(inputs, idMap, {
        existingBlockIds: existingIds,
        existingConnectors: sheet.connectors,
      });
      sheet.connectors.push(...connectors);
      state.counts.connectors += connectors.length;
      return { ok: true, result: { created: connectors.length, rejected } };
    },
    create_group: (args, state) => {
      const sheetRef = s(args.sheet_ref) || s(args.sheet_id);
      const sheetId = state.sheetsByRef.get(sheetRef) ?? sheetRef;
      const sheet = workspace.current.sheets.find(x => x.id === sheetId);
      if (!sheet) return { ok: false, error: `unknown-sheet: ${sheetRef}` };
      const refs = a<string>(args.block_ids);
      const blockIds = refs.map(r => state.blockIdMap.get(r) ?? r);
      const group = createGroupDraft({
        blockIds,
        color: s(args.color) || '#6366f1',
        name: s(args.name) || undefined,
        collapsed: args.collapsed === true,
        collapsedX: typeof args.collapsed_x === 'number' ? args.collapsed_x : undefined,
        collapsedY: typeof args.collapsed_y === 'number' ? args.collapsed_y : undefined,
      });
      sheet.groups.push(group);
      return { ok: true, result: { groupId: group.id } };
    },
    create_cross_sheet_link: (args, state) => {
      const fromIdx = n(args.from_sheet_index);
      const toIdx = n(args.to_sheet_index);
      const fromRef = s(args.from_block_ref) || s(args.from_block_id);
      const toRef = s(args.to_block_ref) || s(args.to_block_id);
      const fromBlockId = state.blockIdMap.get(fromRef) ?? fromRef;
      const toBlockId = state.blockIdMap.get(toRef) ?? toRef;
      const res = createCrossSheetLink({
        fromSheetIndex: fromIdx,
        toSheetIndex: toIdx,
        fromBlockId,
        toBlockId,
        fromPort: args.from_port as PortSide | undefined,
        toPort: args.to_port as PortSide | undefined,
        label: s(args.label) || undefined,
      });
      if (res.ok === false) return { ok: false, error: res.error };
      const csc = res.connector as CrossSheetConnector;
      workspace.current.crossSheetConnectors = [
        ...(workspace.current.crossSheetConnectors ?? []),
        csc,
      ];
      state.counts.crossSheetLinks++;
      return { ok: true, result: { linkId: csc.id } };
    },
    validate_diagram: (_args, state) => {
      const report = validateDiagram(workspace.current);
      state.latestViolations = report.violations;
      return { ok: report.ok, result: report };
    },
  };
}
